import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import BottomTabNav from '../shared/components/BottomTabNav';
import { formatCurrency, getUserCurrency, formatCurrencySync, getCurrencyByLocation, setUserCurrency as saveUserCurrency } from '../shared/utils/currency';
import useCurrencyCache from '../hooks/useCurrencyCache';
import { dataService } from '../services/dataService';
import { authService } from '../config/supabase';
import './CustomerCart.css';

const CustomerCart = () => {
  const { items: cartItems, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();
  const { isLoggedIn } = useAuth();
  
  // Use currency cache hook
  const { formatCurrency: formatCurrencyCached } = useCurrencyCache();
  const navigate = useNavigate();
  const [taxRate, setTaxRate] = useState(8.00); // Default tax rate (8%)
  const [userCurrency, setUserCurrency] = useState('PKR'); // Location-based currency

  // Load user location and currency
  useEffect(() => {
    const loadUserCurrency = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        const email = currentUser?.email;
        const meta = currentUser?.user_metadata || {};

        let city = '';
        let country = '';
        let currency = '';

        if (email) {
          const customerRecord = await dataService.getCustomerByEmail(email).catch(() => null);
          if (customerRecord) {
            city = customerRecord.city || '';
            country = customerRecord.country || '';
            currency = customerRecord.currency || '';
          }
        }

        if (!city) city = meta.city || '';
        if (!country) country = meta.country || '';
        if (!currency) currency = meta.currency || '';

        // Get currency from location if not set
        const derived = await getCurrencyByLocation(country, city);
        const finalCurrency = currency || derived || getUserCurrency();
        setUserCurrency(finalCurrency);
        try { saveUserCurrency(finalCurrency); } catch (_) {}
      } catch (error) {
        console.error('Error loading user currency:', error);
        setUserCurrency(getUserCurrency());
      }
    };

    loadUserCurrency();
  }, []);

  // Fetch tax rate from vendor/shop/pharmacy
  useEffect(() => {
    const fetchTaxRate = async () => {
      if (!cartItems || cartItems.length === 0) return;
      
      try {
        const firstItem = cartItems[0];
        let tax = 8.00; // Default fallback
        
        if (firstItem.type === 'food') {
          const foodItems = await dataService.getFoodItems();
          const foodItem = foodItems.find(item => item.id === firstItem.id);
          if (foodItem?.vendor_id) {
            const vendors = await dataService.getVendors();
            const vendor = vendors.find(v => v.id === foodItem.vendor_id);
            if (vendor?.tax_rate !== undefined && vendor?.tax_rate !== null) {
              tax = parseFloat(vendor.tax_rate);
            }
          }
        } else if (firstItem.type === 'grocery') {
          const groceryItems = await dataService.getGroceryItems();
          const groceryItem = groceryItems.find(item => item.id === firstItem.id);
          if (groceryItem?.shop_id) {
            const shops = await dataService.getShops();
            const shop = shops.find(s => s.id === groceryItem.shop_id);
            if (shop?.tax_rate !== undefined && shop?.tax_rate !== null) {
              tax = parseFloat(shop.tax_rate);
            }
          }
        } else if (firstItem.type === 'pharmacy') {
          const pharmacyItems = await dataService.getPharmacyItems();
          const pharmacyItem = pharmacyItems.find(item => item.id === firstItem.id);
          if (pharmacyItem?.pharmacy_id) {
            const pharmacies = await dataService.getPharmacies();
            const pharmacy = pharmacies.find(p => p.id === pharmacyItem.pharmacy_id);
            if (pharmacy?.tax_rate !== undefined && pharmacy?.tax_rate !== null) {
              tax = parseFloat(pharmacy.tax_rate);
            }
          }
        }
        
        setTaxRate(tax);
      } catch (error) {
        console.error('Error fetching tax rate:', error);
      }
    };
    
    fetchTaxRate();
  }, [cartItems]);

  const calculateTax = () => {
    return getCartTotal() * (taxRate / 100); // Use dynamic tax rate
  };

  const calculateTotal = () => {
    return getCartTotal() + calculateTax();
  };

  const handleCheckout = () => {
    // Check if user is logged in before proceeding to checkout
    if (!isLoggedIn) {
      // Redirect to login with return path to checkout
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }
    // Navigate to checkout page
    navigate('/checkout');
  };

  if (cartItems.length === 0) {
    return (
      <div className="App">
        <div className="cart-container">
          <div className="cart-content">
            <div className="empty-cart">
              <div className="empty-cart-animation">
                <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Cart Body */}
                  <path d="M50 60 L60 100 L140 100 L150 60 Z" fill="#5B3EF0" opacity="0.1" stroke="#5B3EF0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <animate attributeName="opacity" values="0.1;0.2;0.1" dur="2s" repeatCount="indefinite"/>
                  </path>
                  
                  {/* Cart Handle */}
                  <path d="M40 50 L50 50 L50 60" stroke="#5B3EF0" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  
                  {/* Cart Lines */}
                  <line x1="70" y1="70" x2="75" y2="90" stroke="#5B3EF0" strokeWidth="2" strokeLinecap="round">
                    <animate attributeName="y2" values="90;85;90" dur="1.5s" repeatCount="indefinite"/>
                  </line>
                  <line x1="90" y1="70" x2="95" y2="90" stroke="#5B3EF0" strokeWidth="2" strokeLinecap="round">
                    <animate attributeName="y2" values="90;85;90" dur="1.5s" begin="0.2s" repeatCount="indefinite"/>
                  </line>
                  <line x1="110" y1="70" x2="115" y2="90" stroke="#5B3EF0" strokeWidth="2" strokeLinecap="round">
                    <animate attributeName="y2" values="90;85;90" dur="1.5s" begin="0.4s" repeatCount="indefinite"/>
                  </line>
                  <line x1="130" y1="70" x2="135" y2="90" stroke="#5B3EF0" strokeWidth="2" strokeLinecap="round">
                    <animate attributeName="y2" values="90;85;90" dur="1.5s" begin="0.6s" repeatCount="indefinite"/>
                  </line>
                  
                  {/* Wheels */}
                  <circle cx="75" cy="110" r="8" fill="#5B3EF0">
                    <animate attributeName="r" values="8;9;8" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="125" cy="110" r="8" fill="#5B3EF0">
                    <animate attributeName="r" values="8;9;8" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  
                  {/* Floating Food Icons */}
                  <text x="90" y="35" fontSize="24" opacity="0.6">🍕
                    <animate attributeName="y" values="35;30;35" dur="3s" repeatCount="indefinite"/>
                    <animate attributeName="opacity" values="0.6;0.9;0.6" dur="3s" repeatCount="indefinite"/>
                  </text>
                  <text x="110" y="45" fontSize="20" opacity="0.5">🍔
                    <animate attributeName="y" values="45;40;45" dur="2.5s" repeatCount="indefinite"/>
                    <animate attributeName="opacity" values="0.5;0.8;0.5" dur="2.5s" repeatCount="indefinite"/>
                  </text>
                  <text x="70" y="40" fontSize="18" opacity="0.4">🍟
                    <animate attributeName="y" values="40;35;40" dur="2.8s" repeatCount="indefinite"/>
                    <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.8s" repeatCount="indefinite"/>
                  </text>
                  
                  {/* Sparkles */}
                  <circle cx="160" cy="70" r="2" fill="#5B3EF0">
                    <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="165" cy="80" r="2" fill="#5B3EF0">
                    <animate attributeName="opacity" values="0;1;0" dur="1.5s" begin="0.5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="40" cy="75" r="2" fill="#5B3EF0">
                    <animate attributeName="opacity" values="0;1;0" dur="1.5s" begin="1s" repeatCount="indefinite"/>
                  </circle>
                </svg>
              </div>
              <h2 className="empty-cart-title">Your cart is empty</h2>
              <p className="empty-cart-subtitle">
                Looks like you haven't added any items to your cart yet. Browse our delicious menu and add your favorites!
              </p>
              <Link to="/menu" className="browse-menu-btn">
                🍽️ Browse Menu
              </Link>
            </div>
          </div>
        </div>
        
        <BottomTabNav />
      </div>
    );
  }

  return (
    <div className="App">
      <div className="cart-container">
        <div className="cart-content">
          <div className="cart-header">
            <h1 className="cart-title">{cartItems.length} Item{cartItems.length !== 1 ? 's' : ''} in cart</h1>
          </div>

          <div className="cart-items">
            {cartItems.map((item) => (
              <div key={item.id} className="cart-item">
                <img 
                  src={item.image} 
                  alt={item.name} 
                  className="cart-item-image" 
                />
                <div className="cart-item-details">
                  <h3 className="cart-item-name">{item.name}</h3>
                  <p className="cart-item-price">{formatCurrencyCached(item.price, userCurrency)}</p>
                  <div className="quantity-controls">
                    <button 
                      className="quantity-btn minus" 
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      type="button"
                    >
                      -
                    </button>
                    <span className="quantity-display">{item.quantity}</span>
                    <button 
                      className="quantity-btn plus" 
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      type="button"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="cart-item-total">
                  <span className="item-total-price">{formatCurrencyCached(item.price * item.quantity, userCurrency)}</span>
                  <button 
                    className="remove-item-btn" 
                    onClick={() => removeFromCart(item.id)}
                    type="button"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <div className="summary-row">
              <span>Subtotal</span>
              <span>{formatCurrencyCached(getCartTotal(), userCurrency)}</span>
            </div>
            <div className="summary-row">
              <span>Tax ({taxRate}%)</span>
              <span>{formatCurrencyCached(calculateTax(), userCurrency)}</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>{formatCurrencyCached(calculateTotal(), userCurrency)}</span>
            </div>
            
            <div className="cart-actions">
              <button className="clear-cart-btn" onClick={clearCart} type="button">
                Clear Cart
              </button>
              <button className="checkout-btn" onClick={handleCheckout}>
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <BottomTabNav />
    </div>
  );
};

export default CustomerCart;
